import React from "react";
import { useHistory } from "react-router-dom";

import { Badge } from "components/Graphs/Badge";
import { cx } from "components/Graphs/utils";
import { GridBackground } from "./grid-background";
import { PIPELINES, type Pipeline } from "constants/pipelines";

type PhysicalAISectionProps = {
  deployPath?: "/launchable/deploy" | "/launchable/deploy/now";
  showLearnMore?: boolean;
};

function PhysicalAIMarketplaceCard({
  pipeline,
  deployPath = "/launchable/deploy",
}: {
  pipeline: Pipeline;
  deployPath?: PhysicalAISectionProps["deployPath"];
}) {
  const deployUrl = `${window.location.origin}${deployPath}?launchableID=${encodeURIComponent(pipeline.launchableId)}`;

  return (
    <GridBackground className="h-full" deployUrl={deployUrl}>
      <div
        className={cx(
          "border border-zinc-800 bg-primaryBg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg h-full flex flex-col justify-between rounded-md",
        )}
      >
        <div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {pipeline.categories.slice(0, 2).map((cat) => (
              <Badge key={cat} variant="secondary" className="text-xs">
                {cat}
              </Badge>
            ))}
          </div>

          <h1 className="text-lg text-primary transition-colors">
            {pipeline.title}
          </h1>
          <p className="text-sm text-secondary">
            Powered by{" "}
            <span className="text-highlight font-medium">
              {pipeline.poweredBy}
            </span>
          </p>
          <div className="mt-4">
            <p className="text-sm text-secondary line-clamp-2">
              {pipeline.description}
            </p>
          </div>
        </div>
      </div>
    </GridBackground>
  );
}

export function PhysicalAISection({
  deployPath = "/launchable/deploy",
  showLearnMore = false,
}: PhysicalAISectionProps = {}) {
  const history = useHistory();

  return (
    <section>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-primary">Physical AI</h2>
        {showLearnMore && (
          <button
            className="text-sm text-highlight hover:underline"
            onClick={() => history.push("/physical-ai")}
          >
            Learn more
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PIPELINES.map((pipeline) => (
          <PhysicalAIMarketplaceCard
            key={pipeline.launchableId}
            pipeline={pipeline}
            deployPath={deployPath}
          />
        ))}
      </div>
    </section>
  );
}
